import * as THREE from 'three';
import manifest from '../../data/asset-manifest.json';
import { ModelLibrary } from './modelLibrary';

type Lease = { root: THREE.Group; release(): void };
type ModelKey = keyof typeof manifest;
type Status = { loading: number; failed: number };
type LodRecord = { slot: THREE.Object3D; high: THREE.Group; low?: THREE.Group; lowRequested: boolean; lowActive: boolean; key: ModelKey; generation: number };

export function useLowDetail(distance: number, wasLow: boolean): boolean {
  return distance > (wasLow ? 10 : 12);
}

/** Replaces only visual children. Interaction proxies and scenario IDs never change. */
export class FurnitureModels {
  private generation = 0;
  private leases: { slot: THREE.Object3D; lease: Lease; fallback: THREE.Object3D[] }[] = [];
  private failures = new Set<THREE.Object3D>();
  private root: THREE.Object3D | null = null;
  private pending = 0;
  private disposed = false;
  private lods: LodRecord[] = [];
  private readonly cameraPosition = new THREE.Vector3();
  private readonly objectPosition = new THREE.Vector3();

  update(camera: THREE.Camera): void {
    camera.getWorldPosition(this.cameraPosition);
    for (const record of this.lods) {
      record.slot.getWorldPosition(this.objectPosition);
      const far = useLowDetail(this.cameraPosition.distanceTo(this.objectPosition), record.lowActive);
      if (far && !record.lowRequested) this.loadLow(record);
      record.lowActive = far && !!record.low;
      record.high.visible = !record.lowActive;
      if (record.low) record.low.visible = record.lowActive;
    }
  }

  get detailCounts(): { high: number; low: number } {
    return { high: this.lods.filter(record => !record.lowActive).length, low: this.lods.filter(record => record.lowActive).length };
  }

  constructor(
    private readonly library: Pick<ModelLibrary, 'acquire' | 'dispose'> = new ModelLibrary(),
    private readonly onStatus: (status: Status) => void = () => {},
    private readonly onReplaced: () => void = () => {},
  ) {}

  attach(root: THREE.Object3D): void {
    this.clear();
    this.root = root;
    const slots: THREE.Object3D[] = [];
    root.traverse(object => { if (object.name.startsWith('asset-slot:')) slots.push(object); });
    for (const slot of slots) this.load(slot, this.generation);
  }

  retry(): void {
    if (this.disposed || !this.root) return;
    const failed = [...this.failures];
    this.failures.clear();
    for (const slot of failed) this.load(slot, this.generation);
  }

  private load(slot: THREE.Object3D, generation: number): void {
    const key = slot.name.slice('asset-slot:'.length) as ModelKey;
    const asset = manifest[key];
    if (!asset) return;
    this.pending++;
    this.report();
    void this.library.acquire(`${import.meta.env.BASE_URL}${asset.url}`).then(lease => {
      if (this.disposed || generation !== this.generation) { lease.release(); return; }
      const size = slot.userData.assetSize as number[];
      const offset = slot.userData.assetOffset as [number, number, number];
      lease.root.scale.set(size[0] / asset.dimensions[0], size[1] / asset.dimensions[1], size[2] / asset.dimensions[2]);
      lease.root.position.set(...offset);
      lease.root.traverse(object => {
        if (object instanceof THREE.Mesh) { object.castShadow = true; object.receiveShadow = true; }
      });
      const fallback = [...slot.children];
      fallback.forEach(child => { child.visible = false; });
      slot.add(lease.root);
      this.leases.push({ slot, lease, fallback });
      this.lods.push({ slot, high: lease.root, lowRequested: false, lowActive: false, key, generation });
      this.onReplaced();
    }).catch(error => {
      if (this.disposed || generation !== this.generation) return;
      this.failures.add(slot);
      console.warn(`[models] ${key}: 簡易モデルで継続します`, error);
    }).finally(() => {
      if (this.disposed || generation !== this.generation) return;
      this.pending--;
      this.report();
    });
  }

  private loadLow(record: LodRecord): void {
    record.lowRequested = true;
    const asset = manifest[record.key];
    void this.library.acquire(`${import.meta.env.BASE_URL}${asset.lowUrl}`).then(lease => {
      if (this.disposed || record.generation !== this.generation) { lease.release(); return; }
      lease.root.scale.copy(record.high.scale);
      lease.root.position.copy(record.high.position);
      lease.root.visible = false;
      lease.root.traverse(object => {
        if (object instanceof THREE.Mesh) { object.castShadow = true; object.receiveShadow = true; }
      });
      record.slot.add(lease.root);
      record.low = lease.root;
      this.leases.push({ slot: record.slot, lease, fallback: [] });
      this.onReplaced();
    }).catch(error => {
      if (!this.disposed && record.generation === this.generation)
        console.warn(`[models] ${record.key}: LOD読込失敗、高精細表示で継続`, error);
    });
  }

  /** Must run before sceneGraph.disposeGroup: the library owns loaded resources. */
  clear(): void {
    this.generation++;
    for (const { slot, lease, fallback } of this.leases) {
      slot.remove(lease.root);
      lease.release();
      fallback.forEach(child => { child.visible = true; });
    }
    this.leases = [];
    this.lods = [];
    this.failures.clear();
    this.pending = 0;
    this.root = null;
    this.report();
  }

  dispose(): void {
    this.disposed = true;
    this.clear();
    this.library.dispose();
  }

  private report(): void { this.onStatus({ loading: this.pending, failed: this.failures.size }); }
}
